'use client';

import Image from 'next/image';
import { HTMLAttributes, PropsWithChildren } from 'react';
import { useSearchParams } from 'next/navigation';

import MapPinIcon from '../Icons/MapPin';
import Bed from '../Icons/Bed';
import Bath from '../Icons/Bath';
import VillagePromotionLabel from '../VillagePromotionLabel';

import { cn } from '@/lib/utils';
import guests from '../../../public/guests.png';
import { Village } from '@/services/types';

import { IRegionZoneDictonary } from '@/dictionaries/utils/region-zone/types';
import { ICurrencyDictonary } from '@/dictionaries/utils/currency/types';
import { IVillageDictionary } from '@/dictionaries/pages/village/types';
import { VillageFeatureTranslation } from '@/services/types';
import { getVillageLink, villageHasVisiblePromotion } from '@/utils/village';
import getMediaFilterUrl from '@/utils/get-media-filter';
import useCurrency from '@/hooks/useCurrency';
import useFilterContext from '@/hooks/useFilterContext';
import { useLocale } from '@/hooks/useLocale';

export type VillageCardDictonary = {
  regionZoneDict: IRegionZoneDictonary;
  currencyDict: ICurrencyDictonary;
  villageDict: IVillageDictionary;
};

type Props = PropsWithChildren &
  HTMLAttributes<HTMLDivElement> & {
    infos: Village;
    displayingMap?: boolean;
    dicts: VillageCardDictonary;
    className?: string;
    atFeaturedVillages?: boolean;
    customCurrency?: string;
  };

export default function VillageCardRoot({
  infos,
  displayingMap,
  children,
  className,
  dicts,
  atFeaturedVillages,
  customCurrency,
  ...props
}: Props) {
  const lang = useLocale();
  const { currency } = useCurrency();
  const searchParams = useSearchParams();
  const { quantities: filterGuests, selectedDates: filterDates } =
    useFilterContext();

  const params = new URLSearchParams(Array.from(searchParams.entries()));
  const startDateParam = params.get('startDate');
  const endDateParam = params.get('endDate');

  const selectedGuests = getSelectedGuests();

  const hasDatesUrlParams = !!startDateParam && !!endDateParam;
  const hasFilterDatesParams =
    !!filterDates && !!filterDates.start && !!filterDates.end;

  const shouldHideFromLabel = hasDatesUrlParams || hasFilterDatesParams;

  const shouldHidePerWeekLabel =
    (selectedGuests === 0 && (hasDatesUrlParams || hasFilterDatesParams)) ||
    shouldHideFromLabel;

  const villageLink = getVillageLink({
    lang,
    slug: infos.slug,
    region: infos.zone.region.code,
    filterGuests,
    filterDates,
  });

  const villageMainFeatures = infos.features?.filter(({ feature }) => {
    return feature?.type ? feature.type === 'main_feature' : false;
  });

  const villageSubtitle = `${infos.city} (${
    dicts.regionZoneDict[infos.zone.code]
  })`;

  const villageMedia = infos.media.filter(
    (medias) => medias.type == 'cover',
  )[0];

  const villageImgCover =
    getMediaFilterUrl({
      mediaFilters: villageMedia?.mediaFilters || [],
      filterName: 'villa_card_2x',
    }) ??
    getMediaFilterUrl({
      mediaFilters: villageMedia?.mediaFilters || [],
      filterName: 'villa_card_2x_webp',
    }) ??
    getMediaFilterUrl({
      mediaFilters: villageMedia?.mediaFilters || [],
      filterName: '1920w_webp',
    }) ??
    '';

  const villagePrice = Math.floor(infos.calculatedPrice);
  const discountedPrice = Math.floor(infos.discountedPrice);

  const villagePromotionTitle = infos.translations.find((translation) => {
    const currentLang = ['it', 'fr', 'de', 'en'].includes(lang) ? lang : 'en';
    return translation.locale === currentLang;
  })?.promotionTitle;

  const hasVisiblePromotion = villageHasVisiblePromotion(infos.deals);

  function getMainFeaturesTranslated() {
    let translatedFeatures: VillageFeatureTranslation[] = [];
    let currentLang = lang;

    if (lang != 'fr' && lang != 'de' && lang != 'it') {
      currentLang = 'en';
    }

    villageMainFeatures.flatMap(({ feature }) => {
      const filtered = feature.featureTranslation.filter((translation) => {
        return translation.locale === currentLang;
      });
      translatedFeatures.push(...filtered);
    });

    return translatedFeatures;
  }

  function formatVillagePrice(price: number) {
    let formattedPrice = price.toLocaleString('en-US');

    if (lang === 'it' || lang === 'de') {
      formattedPrice = formattedPrice.replace(/,/g, '.');
    }

    if (lang === 'fr') {
      formattedPrice = formattedPrice.replace(/,/g, '');
    }

    const currencyMap: { [key: string]: string } = {
      eur: '€',
      usd: '$',
      gbp: '£',
    };

    if (customCurrency) {
      return `${currencyMap[customCurrency]} ${formattedPrice}`;
    }

    return `${currency.symbol} ${formattedPrice}`;
  }

  function getSelectedGuests() {
    if (filterGuests) {
      const { adults, children, infants } = filterGuests;

      return adults + children + infants;
    }

    return 0;
  }

  return (
    <div
      id={infos.title}
      {...props}
      className={cn(
        'villa-card w-full pb-4 md:pb-[1.875rem] h-[inherit]',
        className,
        {
          displayingMap,
        },
        {
          'w-auto text-center desktop:pb-0 ml-0 mr-0 desktop:mx-0 ':
            atFeaturedVillages,
        },
      )}>
      <article
        className={cn(
          'inline-block w-full h-full bg-white shadow-default desktop:hover:shadow-[0_10px_20px_0_rgba(0,0,0,.3)] desktop:transition-all desktop:duration-300',
          {
            'w-4/5 desktop:w-[calc(100%-15px)] desktop:hover:translate-y-[-3%] desktop:transition-all desktop:duration-300 desktop:hover:shadow-[0_20px_40px_0_rgba(0,0,0,.3)]':
              atFeaturedVillages,
          },
        )}>
        <a
          tabIndex={atFeaturedVillages ? -1 : undefined}
          className="block"
          href={villageLink}
          target="_blank">
          <div className="w-full h-[420px] relative overflow-hidden">
            {infos.isNew && (
              <VillagePromotionLabel
                isNew
                isPromotion={false}
                newVillaLabel={dicts.villageDict.newVilla}
              />
            )}
            {hasVisiblePromotion && villagePromotionTitle && !infos.isNew && (
              <VillagePromotionLabel
                isPromotion
                isNew={false}
                promotionTitle={villagePromotionTitle}
              />
            )}
            <div className="block desktop:hidden w-fit -mr-2 absolute group is-absolute top-0 right-4">
              {children}
            </div>
            <div className="bg-gray-100 w-full h-full">
              <Image
                src={villageImgCover}
                alt={infos.title}
                fill
                className="w-full max-w-[100%] h-full object-cover inline-block align-middle"
                sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
                style={{
                  objectFit: 'cover',
                }}
              />
            </div>
            <div className="absolute left-0 bottom-0 w-full h-[90px] z-[15] overflow-hidden before:content-[''] before:absolute before:-z-10 before:left-0 before:bottom-0 before:w-full before:h-full before:bg-black/30 before:backdrop-blur-[2px]">
              <div className="relative h-[90px] p-[15px] pl-2 laptop:pl-[15px] text-white flex flex-row flex-nowrap justify-between w-full">
                <div className="text-left">
                  <h3 className="text-2xl laptop:text-medium font-semibold font-title ">
                    {infos.title}
                  </h3>
                  <div className="text-[13px] laptop:text-[15px] desktop:text-base flex items-center">
                    <MapPinIcon className="w-4 h-4 min-w-4 mr-1" />
                    <p className="flex-1">{villageSubtitle}</p>
                  </div>
                </div>
                <div className="hidden desktop:block w-fit -mr-2 bottom-[94px] right-4">
                  {children}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white text-graphite p-[15px]">
            <div className="flex flex-col flex-wrap desktop:flex-row">
              <div className="text-base font-noe-regular leading-[1.62] text-left">
                <span>
                  {!shouldHideFromLabel && `${dicts.currencyDict.from}`}
                  {infos.calculatedPrice > infos.discountedPrice && (
                    <span className="whitespace-nowrap text-xl ml-2 font-bold leading-[1.38] font-title line-through text-gray-700 -mr-[5px]">
                      {formatVillagePrice(villagePrice)}
                    </span>
                  )}
                  <span className="whitespace-nowrap text-2xl ml-2 font-bold leading-[1.38] font-title">
                    {formatVillagePrice(discountedPrice)}
                  </span>
                  {!shouldHidePerWeekLabel && `/${dicts.currencyDict.perWeek}`}
                </span>
              </div>
              <ul
                className={cn(
                  'mx-[-12px] list-none py-[4px] flex desktop:ml-auto',
                  {
                    'mx-auto desktop:mx-0 desktop:ml-auto': atFeaturedVillages,
                  },
                )}>
                <li className="flex items-center mx-[12px]">
                  <Image src={guests} alt="Guests icon image" />
                  <span className="ml-2">{infos.sleeps}</span>
                </li>
                <li className="flex items-center mx-[12px]">
                  <Bath />
                  <span className="ml-2">{infos.bedrooms}</span>
                </li>
                <li className="flex items-center mx-[12px]">
                  <Bed />
                  <span className="ml-2">{infos.bathrooms}</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-100 w-full min-h-[40px] mb-auto mt-[20px] text-center text-sm leading-[1.71] overflow-hidden text-ellipsis rounded-[5px] py-[8px] px-[10px] [&>:not(:first-child)]:before:content-['/'] [&>:not(:first-child)]:before:inline-block [&>:not(:first-child)]:before:mx-[8px]">
              {getMainFeaturesTranslated().map(({ title }, index) => (
                <span key={index}>{title}</span>
              ))}
            </div>
          </div>
        </a>
      </article>
    </div>
  );
}
